/**
 * Onboarding scan/demo-intro beat guard self-heal — extracted from the
 * ChatBox driver effect (#3640, #3651 review).
 *
 * The driver effect appends the scan/demo-intro beat optimistically and records
 * a long-lived "fire once" key (`scan:${convId}` / `demo-intro:${convId}`) in a
 * ref Set. On the first page load after connecting Google the Welcome
 * conversation is NOT bypassed, so an in-flight server GET can resolve AFTER the
 * optimistic append but BEFORE its POST persists, replacing local state with
 * rows that lack the just-appended beat — wiping it. The stale guard key then
 * makes the driver re-fire take the early return, so the beat is never
 * re-appended and onboarding stalls below the greeting until a manual refresh.
 *
 * `reconcileBeatGuard` drops a stale guard key when the guarded beat is no
 * longer present in `beatMessages` (a real wipe), so the append re-fires.
 *
 * StrictMode safety: React's dev double-invoke runs the effect twice against the
 * SAME committed render — `beatMessages` is the same array reference both times
 * (no re-render in between, `setMessages` has not flushed). Run 1 appends the
 * beat + records the key; if reconciliation ran again on run 2 it would see the
 * key present and the beat still absent (state not flushed) and wrongly delete
 * the key, causing a SECOND append. We therefore reconcile at most once per
 * distinct `beatMessages` reference: `lastReconciledRef` records the snapshot we
 * last reconciled against, and run 2 (same reference) is a no-op. A real wipe
 * produces a NEW `beatMessages` array (the server GET re-rendered with replaced
 * `messages`), so reconciliation runs and heals as intended.
 */

import type { MutableRefObject } from 'react';
import type { BeatKind } from '../OnboardingBeats';

export interface ReconcileBeatGuardDeps {
  /** Stable conversation id the beats are keyed on. */
  convId: string;
  /** Current onboarding-beat messages (a fresh array per re-render). */
  beatMessages: readonly unknown[];
  /** Long-lived "fire once" guard keyed `scan:${convId}` / `demo-intro:${convId}`. */
  beatAppendGuard: MutableRefObject<Set<string>>;
  /** Records the `beatMessages` reference last reconciled (StrictMode dedup). */
  lastReconciledRef: MutableRefObject<readonly unknown[] | null>;
  /** Whether a beat of the given kind is present in `beatMessages`. */
  hasBeatKind: (kind: BeatKind) => boolean;
}

/**
 * Drop stale `scan` / `demo-intro` guard keys when their beat has been wiped
 * from the transcript, so the driver's append can re-fire. No-op on the
 * StrictMode synchronous double-invoke (same `beatMessages` reference) and when
 * the transcript is empty (`beatMessages.length === 0`, the pre-flush mount).
 */
export function reconcileBeatGuard(deps: ReconcileBeatGuardDeps): void {
  const { convId, beatMessages, beatAppendGuard, lastReconciledRef, hasBeatKind } = deps;
  // Only a real, committed re-render that still has beats can be a wipe. The
  // empty snapshot is the pre-flush mount; the same reference twice is the
  // StrictMode double-invoke — neither must delete a freshly-added key.
  if (beatMessages.length === 0 || beatMessages === lastReconciledRef.current) return;
  lastReconciledRef.current = beatMessages;
  const scanKey = `scan:${convId}`;
  if (beatAppendGuard.current.has(scanKey) && !hasBeatKind('scan')) beatAppendGuard.current.delete(scanKey);
  const demoKey = `demo-intro:${convId}`;
  if (beatAppendGuard.current.has(demoKey) && !hasBeatKind('demo-intro')) beatAppendGuard.current.delete(demoKey);
}
